import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const CircleCorrectLetter: React.FC = () => {
    const [targetLetter, setTargetLetter] = useState("");
    const [letterGrid, setLetterGrid] = useState<string[]>([]);
    const [selectedLetters, setSelectedLetters] = useState<number[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Circle the Correct Letter – Nursery English Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: string[]) => {
        return array.sort(() => Math.random() - 0.5);
    };

    const generateNewQuestion = () => {
        const newTarget = letters[Math.floor(Math.random() * letters.length)];
        setTargetLetter(newTarget);

        const options = Array.from({ length: 14 }, () => letters[Math.floor(Math.random() * letters.length)]);

        // Ensure correct letter appears at least twice
        options.push(newTarget, newTarget);

        setLetterGrid(shuffleArray(options));
        setSelectedLetters([]);
        setMessage("");
    };

    const handleLetterClick = (index: number) => {
        let newSelectedLetters = [...selectedLetters];
        if (newSelectedLetters.includes(index)) {
            newSelectedLetters = newSelectedLetters.filter(i => i !== index);
        } else {
            newSelectedLetters.push(index);
        }
        setSelectedLetters(newSelectedLetters);

        // Check if all correct letters are selected
        const correctSelections = letterGrid.reduce((acc, l, i) => l === targetLetter ? [...acc, i] : acc, [] as number[]);
        if (newSelectedLetters.length === correctSelections.length && newSelectedLetters.every(i => correctSelections.includes(i))) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
            }, 5000);
        } else {
            setMessage("");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Practice letter recognition with the "Circle the Correct Letter" activity on Mamas Guidance—an interactive exercise for nursery kids to identify the correct beginning letter of words through fun visual prompts.' />
            </Helmet>
            <div className="contentPart additionWord matchImage letterOption match-container" style={{ textAlign: "center", padding: "20px" }}>
                {
                    showImage === false ?
                        <>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Circle the Correct Letter</h1>

                                    <p>
                                        Make phonics fun and interactive with our <strong>Circle the Correct Letter</strong> worksheet! This engaging activity helps nursery and preschool children <strong>recognize beginning sounds</strong> and match them to the correct alphabet letter.
                                    </p>

                                    <h2>🔤 What’s in the Worksheet?</h2>
                                    <p>
                                        Each question presents a colorful image of a familiar object like <b><em>apple, sun, ball, dog</em></b>, etc. Below each image are three letter options. Children are asked to <strong>circle the correct letter</strong> that represents the beginning sound of the object.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify beginning sounds of common words</li>
                                        <li>Recognize letters and their corresponding sounds</li>
                                        <li>Improve phonemic awareness and early literacy</li>
                                        <li>Enhance visual and listening comprehension</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Point to the picture and say the word aloud.</li>
                                        <li>Emphasize the beginning sound (e.g., <strong>“B-b-b-ball”</strong>).</li>
                                        <li>Ask your child to choose the correct starting letter from the three options.</li>
                                        <li>Encourage them to say the letter and word together.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Object With Letter</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/match-letter"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Letters</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/english-language/before-after-between-letters"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Letters</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Is Important</h2>
                                    <p>
                                        Understanding beginning sounds is one of <strong>the core foundations of reading</strong>. By circling the correct letter, children actively connect <strong>visual objects</strong> with alphabet sounds — a key part of phonics education.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Circle the Correct Letter</h1>

                                <p>
                                    Make phonics fun and interactive with our <strong>Circle the Correct Letter</strong> worksheet! This engaging activity helps nursery and preschool children <strong>recognize beginning sounds</strong> and match them to the correct alphabet letter.
                                </p>

                                <h2>🔤 What’s in the Worksheet?</h2>
                                <p>
                                    Each question presents a colorful image of a familiar object like <b><em>apple, sun, ball, dog</em></b>, etc. Below each image are three letter options. Children are asked to <strong>circle the correct letter</strong> that represents the beginning sound of the object.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify beginning sounds of common words</li>
                                    <li>Recognize letters and their corresponding sounds</li>
                                    <li>Improve phonemic awareness and early literacy</li>
                                    <li>Enhance visual and listening comprehension</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Point to the picture and say the word aloud.</li>
                                    <li>Emphasize the beginning sound (e.g., <strong>“B-b-b-ball”</strong>).</li>
                                    <li>Ask your child to choose the correct starting letter from the three options.</li>
                                    <li>Encourage them to say the letter and word together.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/match-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Letters</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/before-after-between-letters"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Letters</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Understanding beginning sounds is one of <strong>the core foundations of reading</strong>. By circling the correct letter, children actively connect <strong>visual objects</strong> with alphabet sounds — a key part of phonics education.
                                </p>
                            </article>
                            <h3>Circle all the <span style={{ fontSize: '30px' }}>{targetLetter}</span> letters</h3>
                            <div
                                className="mb-4 letter-grid"
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4, 50px)",
                                    gap: "10px",
                                    justifyContent: "center",
                                }}
                            >
                                {letterGrid.map((letter, index) => (
                                    <div
                                        key={index}
                                        className={`letter-box ${selectedLetters.includes(index) ? (letter === targetLetter ? "correct" : "wrong") : ""}`}
                                        onClick={() => handleLetterClick(index)}
                                        style={{
                                            width: "50px",
                                            height: "50px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            border: "2px solid #000",
                                            borderRadius: "50%",
                                            fontSize: "20px",
                                            cursor: "pointer",
                                            backgroundColor: selectedLetters.includes(index)
                                                ? letter === targetLetter
                                                    ? "lightgreen"
                                                    : "lightcoral"
                                                : "white",
                                        }}
                                    >
                                        {letter}
                                    </div>
                                ))}
                            </div>
                            {message && <h4 style={{ marginTop: "20px", color: "green" }}>{message}</h4>}
                        </>
                        :
                        <>
                            <div className="contentPart showMiddle">
                                <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                            </div>
                        </>
                }
            </div>

        </>
    );
};

export default CircleCorrectLetter;
